<template>
 <v-container fluid ma-0 pa-0 px-4 >
    <Loading :isLoading="searching">
    <div class="fixed-header" :class="{'mobile': $vuetify.breakpoint.smAndDown}">
        <v-layout  align-center fill-height class="fixed-header-wrapper">
          <v-flex shrink class="text-truncate" pr-4>
            <v-tooltip color="secondary" bottom>
              <template v-slot:activator="{ on }">
                <h2 v-on="on" class="p-relative text-truncate" >
                  Search results for <span class="font-weight-bold">{{searchTerm}}</span>
                </h2>
              </template>
                <span>Import</span>
              </v-tooltip>
            <h4 class="pt-1 sub-header ">All case templates - {{totalResults}} results</h4>
          </v-flex>
          <v-spacer></v-spacer>
          <search-experience-banner mode="classic" align="header" v-on:switch="$emit('switch-search')"></search-experience-banner>
        </v-layout>
      </div>
    <v-container mt-4 pa-0 fluid class="elevation-2 rounded-3" style="background-color:white">
      <v-tabs
        v-model="currentTab"
        show-arrows
        color="white"
        slider-color="primary"
        class="search-results-tabs">
        <v-tab
          v-for="(result, index) in resultTabs"
          :key="'tab-' + result.caseTypeId + '-' + index"
          class="search-result-tab"
          ripple>
          {{ result.displayName }}
          <span class="search-count-badge secondary white--text">{{ result.total }}</span>
        </v-tab>
        <v-tab v-if="showDocumentsTab" key="documents-tab" class="search-result-tab" ripple>
          Documents
          <span class="search-count-badge secondary white--text">{{ documentTotal }}</span>
        </v-tab>

        <v-tab-item
          v-for="(result, index) in resultTabs"
          :key="'item-' + result.caseTypeId + '-' + index"
          :transition="false"
          :reverse-transition="false">
          <v-divider></v-divider>
          <v-container fluid ma-0 pa-0 class="search-result-pane">
            <v-layout row wrap align-center class="px-3 pt-2">
              <v-flex>
                <h3 class="mb-0">{{ result.displayName }}</h3>
              </v-flex>
              <v-spacer></v-spacer>
              <div class="add-case-btn">
                <v-tooltip top color="secondary">
                  <v-btn
                    slot="activator"
                    icon
                    class="ma-0 mr-0"
                    v-on:click="addNewCase(result.caseTypeId)">
                    <v-icon color="success">add_circle</v-icon>
                  </v-btn>
                  <span>Add a new case</span>
                </v-tooltip>
                <v-menu
                  left offset-y nudge-top="30px"
                  origin="center center" transition="scale-transition">
                  <v-btn
                    flat icon dark
                    slot="activator" class="ma-0 mr-0 pa-0" color="secondary">
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                  <v-list dense>
                    <v-list-tile @click="toggleTasks(index)">
                      <v-icon class="mr-2" color="secondary">done_all</v-icon> {{ taskToggles[index] ? 'Hide tasks' : 'Run a task' }}
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </div>
            </v-layout>
            <v-slide-y-transition>
              <v-container fluid ma-0 pa-0 v-if="taskToggles[index]">
                <RunTasks
                  :caseTypeId="result.caseTypeId"
                  :selections="bucketSelections">
                </RunTasks>
              </v-container>
            </v-slide-y-transition>
            <bucket-table
              :id="'resultsTable-' + result.caseType.code"
              :ref="'resultsTable-' + result.caseType.code"
              :dataProvider="dataProvider"
              :searchData="result"
              :resultTableMode="true"
              :showStatistics="false"
              :currentFilter="tableFilters[index]"
              :taskToggled="taskToggles[index]"
              v-on:selections-update="setBucketSelections">
            </bucket-table>
          </v-container>
        </v-tab-item>
        <v-tab-item v-if="showDocumentsTab" key="documents-item" :transition="false" :reverse-transition="false">
          <v-divider></v-divider>
          <v-container fluid class="pa-3">
            <h3 class="mb-2">
              Documents
              <span v-if="showingDocumentCount < documentTotal" class="sub-header body-1">
                (showing {{ showingDocumentCount }} of {{ documentTotal }})
              </span>
            </h3>
            <document-search-results :documents="documentHits"></document-search-results>
          </v-container>
        </v-tab-item>
      </v-tabs>
    </v-container>
    </Loading>
  </v-container>
</template>

<script>
import { mapState } from "vuex"
import Loading from "@/components/shared/Loading.vue"
import Helpers from "@/helpers.js"
import BucketTable from "@/components/buckets/BucketTable.vue"
import RunTasks from "@/components/buckets/bucket-elements/RunTasks"
import DataProvider from "@/components/buckets/bucket-providers/SearchDataProvider.js"
import DocumentSearchResults from "@/components/search/DocumentSearchResults.vue"
import SearchExperienceBanner from "@/components/search/SearchExperienceBanner.vue"
import { logFeature } from "@/helpers/featureActivity"
import Vue from 'vue'

export default {
  name: "SearchResultsClassic",
  components: { Loading, BucketTable, RunTasks, DocumentSearchResults, SearchExperienceBanner },
  props: {
    dataProvider: {
      type: Object,
      default: function() { return DataProvider }
    },
    helpers: {
      type: Object,
      default: function() { return Helpers }
    }
  },
  data: () => ({
    searching: false,
    currentTab: 0,
    results: [],
    documentSearch: { documents: [], total: 0 },
    taskToggles: [],
    tableFilters: [],
    bucketSelections: []
  }),
  computed: {
    ...mapState({
      searchTerm: state => state.cbroute.params.search_term,
      pageSize: state => state.currentUser.me.preferences.pageSize
    }),
    resultTabs() {
      if (!this.results || this.results.length === 0) {
        return []
      }
      return this.results.filter(function(result) {
        return result && result.total > 0
      })
    },
    documentHits() {
      if (this.documentSearch && this.documentSearch.documents) {
        return this.documentSearch.documents
      }
      return []
    },
    documentTotal() {
      if (this.documentSearch && this.documentSearch.total) {
        return this.documentSearch.total
      }
      return 0
    },
    showingDocumentCount() {
      return this.documentHits.length
    },
    showDocumentsTab() {
      return this.documentTotal > 0 || this.documentHits.length > 0
    },
    totalResults() {
      let total = 0
      if (this.results.length > 0) { this.results.forEach(function(type) { total += type.total }) }
      total += this.documentTotal
      return total
    }
  },
  watch: {
    searchTerm() { if (this.searchTerm) { this.performSearch() } }
  },
  created() {
    // The shared search provider keeps its store reference; this is not component state.
    // eslint-disable-next-line vue/no-mutating-props
    this.dataProvider.store = Vue.store
    if (!this.searching && this.searchTerm) { this.performSearch() }
  },
  methods: {
    addNewCase(caseTypeId) {
      this.$store.dispatch("addCaseViaCaseTypeId", caseTypeId)
    },
    toggleTasks(index) {
      this.taskToggles[index] = !this.taskToggles[index]
      Vue.set(this, "taskToggles", [...this.taskToggles])
    },
    emptyTableFilter() {
      return {
        active: false,
        field: "all_fields",
        term: "",
        itemCount: 0,
        page: 1,
        prevPage: 1,
        currentPage: 1
      }
    },
    async performSearch() {
      this.searching = true
      this.currentTab = 0
      if (this.searchTerm && this.searchTerm.length > 0) {
        const payload = await this.dataProvider.getGlobalSearch(this.searchTerm)
        const results = payload && payload.results ? payload.results : []
        if (results && results.length > 0) {
          results.forEach((result) => {
            const type = this.helpers.getCaseTypeById(this.$store.state, result.caseTypeId)
            result.caseType = type
            if (type && type.displayName) {
              result.displayName = type.displayName
            } else if (type && type.name) {
              result.displayName = type.name
            }
          })
          this.results = results
        } else {
          this.results = []
        }
        if (payload && payload.documents) {
          this.documentSearch = payload.documents
        } else {
          this.documentSearch = { documents: [], total: 0 }
        }
        let documentTotal = 0
        if (this.documentSearch && this.documentSearch.total) {
          documentTotal = this.documentSearch.total
        }
        logFeature("document-search", "gui-results", "Global search returned document hits", {
          cases: this.results.length,
          documents: documentTotal,
        })
        const tabs = this.results.filter(function(result) {
          return result && result.total > 0
        })
        this.taskToggles = tabs.map(function() { return false })
        this.tableFilters = tabs.map(this.emptyTableFilter)
      }
      this.searching = false
    },
    hasValidLink(header, data) {
      if (data === undefined || data === null) { return false }
      if (header.hasLink) {
        if (data.route !== "" && data.route !== undefined) { return true }
      }
      return false
    },
    setBucketSelections(newSelections) {
      this.bucketSelections = newSelections
    }
  }
}
</script>

<style lang="scss" scoped>
.search-count-badge {
  display: inline-block;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  margin-left: 8px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  font-weight: 500;
}

.search-result-tab + .search-result-tab {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.add-case-btn {
  position: static;
  top: auto;
  right: auto;
}

.search-results-tabs {
  max-width: 100%;
}

.search-results-tabs >>> .v-tabs__items,
.search-results-tabs >>> .v-tabs__content {
  max-width: 100%;
  overflow: hidden;
}

.search-result-pane {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
</style>
